import {
  Children, isValidElement, useEffect, useId, useMemo, useState, type ReactNode,
} from 'react';
import { FolhaDeBaixo } from './FolhaDeBaixo';
import { plural } from './formato';
import { Icone } from './Icone';
import { useEstreito } from './ui';
import './listas.css';

/**
 * As peças do padrão de lista Carbon Testesolo (onda 270).
 *
 * «Unifique com o Design system Carbon Testesolo e sugira melhorias de
 *  ordenação, listagem, filtros, filtro de data (apenas 1 calendário…)»
 *  — Humberto, 03/10/2026. Aprovado em 04/10 com o canvas «Listas do
 *  LabManager · Carbon Testesolo»: «Depois de definido o padrão vamos
 *  gravar para os próximos módulos».
 *
 * A auditoria das 14 listas achou SEIS formas de filtrar (menus, menus
 * crus, fileiras de botões, abas, caixas de seleção, controle segmentado),
 * três de escolher data, sete listas sem contagem e nenhuma paginação. A
 * causa era a mesma em todas: cada tela montava a sua faixa. Estas peças
 * são a faixa — a tela escolhe quais usa e na ordem do padrão:
 *
 *   ResumoQueFiltra   (opcional) os números que filtram ao clicar
 *   BarraDeLista      busca · filtros em menu · Período · alternâncias
 *   FiltrosAtivos     o que está filtrando, removível, e «N de M»
 *   …a tabela…
 *   RodapeDaLista     a ordem vigente e a paginação
 *
 * ── No celular (onda 309, spec 06 §6, RN-251 d) ─────────────────────
 *
 * «O trabalho começa na primeira tela.» A 412 px a faixa inteira empilhava
 * resumo, busca, Período, Situação, Cliente, OS e Ordenar — o primeiro item
 * da lista ficava a duas telas de rolagem. Abaixo de 640 px as mesmas peças
 * mudam de forma, sem que a tela que as usa mude nada além de dizer quantos
 * filtros estão ativos:
 *
 *   ResumoQueFiltra   uma linha de pílulas («número rótulo») que rola de lado
 *                     dentro dela; as de zero, apagadas (`data-zero`);
 *   BarraDeLista      a busca e um botão «Filtros»; o resto numa folha;
 *   RodapeDaLista     «30 de 225» e «Mostrar mais», de 30 em 30.
 */

/** O que fica na linha da barra no celular: a busca e as ações (`barra-acao`). */
function ficaNaLinha(c: ReactNode): boolean {
  if (!isValidElement(c)) return false;
  if (c.type === BuscaDaBarra) return true;
  /* Uma ação na barra («Novo acesso» do Portal) não é filtro: escondê-la
     atrás de «Filtros» seria esconder o que a tela faz. */
  const classe = (c.props as { className?: unknown }).className;
  return typeof classe === 'string' && /\bbarra-acao\b/.test(classe);
}

/**
 * A barra de ferramentas colada à tabela.
 *
 * No celular, a busca (achada pelo tipo, `BuscaDaBarra`) fica na linha ao
 * lado de um botão «Filtros»; os outros filhos (Período, Situação, Cliente,
 * OS, Ordenar, alternadores) moram na `FolhaDeBaixo`, um embaixo do outro,
 * com «Limpar» e «Ver N resultados». A barra sem nada além da busca não
 * ganha botão. `ativos`, `aoLimpar` e `total` são da tela; quem não passa
 * ganha a folha sem contagem e só com «Ver».
 *
 * `ativos` conta SÓ os filtros que moram na folha (Ruling R13): a busca e
 * a pílula do resumo estão à vista na própria tela, e contá-las no botão
 * mandaria a pessoa abrir a folha para procurar um filtro que não está lá.
 * Pelo mesmo motivo o «Limpar» da folha (`aoLimpar`) tira os filtros da
 * folha e deixa a busca — o «Limpar filtros» da faixa continua tirando tudo.
 */
export function BarraDeLista({ children, rotulo = 'Filtros da lista', ativos = 0, aoLimpar, total, emLinha = false }: {
  children: ReactNode; rotulo?: string;
  /**
   * Tudo na linha, também no celular, sem o botão «Filtros» nem a folha: para
   * a barra de um filtro só (o «Meus» do Radar), em que a folha seria um
   * toque a mais para a única escolha da tela.
   */
  emLinha?: boolean;
  /** Quantos filtros DA FOLHA estão valendo — o número no botão «Filtros». */
  ativos?: number;
  /** Tira os filtros da folha (não a busca) — o «Limpar» da folha. */
  aoLimpar?: () => void;
  /** Quantas linhas o filtro deixa — o «Ver N resultados» da folha. */
  total?: number;
}) {
  const estreito = useEstreito();
  const [aberta, setAberta] = useState(false);
  const idFolha = useId();
  /* A janela cresceu com a folha aberta: no desktop a barra volta inteira, e
     a folha não pode reabrir sozinha quando a janela encolher de novo. */
  useEffect(() => { if (!estreito) setAberta(false); }, [estreito]);

  if (!estreito || emLinha) {
    return <div className="barra-lista" role="search" aria-label={rotulo}>{children}</div>;
  }

  const itens = Children.toArray(children);
  const naLinha = itens.filter(ficaNaLinha);
  const naFolha = itens.filter((c) => !ficaNaLinha(c));

  return (
    <div className="barra-lista" role="search" aria-label={rotulo}>
      {naLinha}
      {naFolha.length > 0 && (
        <>
          <button type="button" className="botao secundario botao-filtros"
            aria-haspopup="dialog" aria-expanded={aberta}
            /* A folha só existe aberta: fechada, o botão não aponta para nada. */
            aria-controls={aberta ? idFolha : undefined}
            aria-label={ativos > 0 ? `Filtros (${plural(ativos, 'ativo', 'ativos')})` : undefined}
            onClick={() => setAberta(true)}>
            <Icone nome="filtro" />
            Filtros
            {ativos > 0 && <span className="selo-filtros">{ativos}</span>}
          </button>
          <FolhaDeBaixo aberta={aberta} titulo="Filtros" classe="folha-filtros" id={idFolha}
            aoFechar={() => setAberta(false)}
            rodape={(
              <>
                {aoLimpar && (
                  <button type="button" className="botao secundario" onClick={aoLimpar}>Limpar</button>
                )}
                <button type="button" className="botao" onClick={() => setAberta(false)}>
                  {total === undefined ? 'Ver' : `Ver ${plural(total, 'resultado', 'resultados')}`}
                </button>
              </>
            )}>
            {naFolha}
          </FolhaDeBaixo>
        </>
      )}
    </div>
  );
}

/** A busca: sempre a primeira peça da barra, e a que cresce. */
export function BuscaDaBarra({ valor, aoMudar, placeholder, rotulo }: {
  valor: string; aoMudar(v: string): void; placeholder: string; rotulo: string;
}) {
  return (
    <label className="barra-busca">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
        strokeWidth="1.6" aria-hidden="true">
        <circle cx="7" cy="7" r="5" /><path d="M11 11l3.5 3.5" />
      </svg>
      <input type="search" value={valor} placeholder={placeholder} aria-label={rotulo}
        onChange={(e) => aoMudar(e.target.value)} />
    </label>
  );
}

/**
 * Sim/não na barra: um botão com estado, nunca uma caixa de seleção solta.
 * O texto diz o que está valendo («✓ Só em aberto»), não o que aconteceria.
 */
export function AlternarDaBarra({ ligado, aoMudar, children }: {
  ligado: boolean; aoMudar(v: boolean): void; children: ReactNode;
}) {
  return (
    <button type="button" className={`barra-alternar${ligado ? ' ativo' : ''}`}
      aria-pressed={ligado} onClick={() => aoMudar(!ligado)}>
      {children}
    </button>
  );
}

export interface FiltroAtivo { chave: string; texto: string; tirar(): void }

/**
 * O que está filtrando agora. Some quando nada filtra — mas a contagem,
 * quando dada, fica sempre: «N de M» é como a pessoa sabe que a lista
 * está cortada.
 */
export function FiltrosAtivos({ itens, aoLimpar, contagem, acao }: {
  itens: FiltroAtivo[]; aoLimpar(): void; contagem?: ReactNode;
  /** Uma ação na linha da contagem — o «Selecionar» das Amostras no celular (309). */
  acao?: ReactNode;
}) {
  if (itens.length === 0 && !contagem && !acao) return null;
  return (
    <div className="filtros-ativos">
      {itens.length > 0 && <span className="rot">Filtrando por</span>}
      {itens.map((f) => (
        <span key={f.chave} className="filtro-ativo">
          {f.texto}
          <button type="button" aria-label={`Tirar o filtro ${f.texto}`} onClick={f.tirar}>×</button>
        </span>
      ))}
      {itens.length > 0 && (
        <button type="button" className="botao discreto" onClick={aoLimpar}>Limpar filtros</button>
      )}
      {contagem && <span className="contagem">{contagem}</span>}
      {acao}
    </div>
  );
}

export interface ItemDoResumo {
  chave: string;
  numero: number;
  rotulo: string;
  aux?: string;
  /** A cor do símbolo — nunca carrega a informação sozinha. */
  tom: 'critico' | 'atencao' | 'andamento' | 'marcador' | 'neutro' | 'ok';
}

/**
 * Os números que filtram. Clicar escolhe o recorte, clicar de novo solta;
 * o escolhido ganha a régua laranja do «você está aqui».
 *
 * O de número 0 leva `data-zero`: no celular (309) a pílula de zero fica
 * apagada e tracejada — continua tocável, mas não disputa o olho com as que
 * têm o que mostrar.
 */
export function ResumoQueFiltra({ itens, ativo, aoEscolher, rotulo }: {
  itens: ItemDoResumo[]; ativo: string | null; aoEscolher(k: string | null): void; rotulo: string;
}) {
  return (
    <section className="resumo-filtra" aria-label={rotulo}>
      {itens.map((i) => (
        <button key={i.chave} type="button" aria-pressed={ativo === i.chave}
          data-recorte={i.chave}
          data-zero={i.numero === 0 ? '' : undefined}
          className={ativo === i.chave ? 'aqui' : ''}
          onClick={() => aoEscolher(ativo === i.chave ? null : i.chave)}>
          <b>{i.numero}</b>
          <span className="rot"><i className={`sim ${i.tom}`} aria-hidden="true" />{i.rotulo}</span>
          {i.aux && <span className="aux">{i.aux}</span>}
        </button>
      ))}
    </section>
  );
}

/** No celular a lista vem de tantas em tantas (RN-251 d). */
const POR_VEZ_NO_CELULAR = 30;

/**
 * A paginação do padrão (a partir de 50 linhas).
 *
 * As listas do LabManager pedem à API no máximo 200–500 linhas e cortavam
 * calado. Paginar no navegador o que já chegou não muda o teto, mas tira a
 * rolagem infinita e, junto com o rodapé, diz o tamanho da lista.
 *
 * No celular (309) ela ACUMULA: 30 por vez, e cada «Mostrar mais» soma mais
 * 30 embaixo das que já estão (`visiveis = linhas.slice(0, pagina * 30)`).
 * Virar a página a 412 px jogava a pessoa de volta ao topo de uma lista
 * nova, e as setas de 40 px no pé da tela eram o alvo mais difícil dela.
 * `acumula` diz ao `RodapeDaLista` qual dos dois rodapés desenhar.
 *
 * `recomeco` é a pergunta que a lista responde — a busca, a pílula e os
 * filtros numa chave só. No celular, quando ela muda, a lista volta para os
 * 30 primeiros: os «90 de 120» de antes eram de outra lista, e quem trocou
 * o filtro quer ver o começo da nova, não o fundo dela. Acima de 640 px a
 * paginação continua como sempre.
 */
export function usePaginacao<T>(linhas: T[], porPagina = 50, recomeco?: string) {
  const acumula = useEstreito();
  const passo = acumula ? POR_VEZ_NO_CELULAR : porPagina;
  const [pagina, setPagina] = useState(1);
  const total = linhas.length;
  const paginas = Math.max(1, Math.ceil(total / passo));
  /* O filtro encolheu a lista: volta para uma página que existe. */
  useEffect(() => { if (pagina > paginas) setPagina(paginas); }, [pagina, paginas]);
  /* A janela trocou de lado da régua: «página 3» não quer dizer a mesma
     coisa nos dois modos — recomeça do começo. */
  useEffect(() => { setPagina(1); }, [acumula]);
  /* A pergunta mudou (no celular): recomeça em 30. Só `recomeco` dispara —
     a troca de régua já tem o efeito de cima. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (acumula) setPagina(1); }, [recomeco]);
  const visiveis = useMemo(
    () => (acumula
      ? linhas.slice(0, pagina * passo)
      : linhas.slice((pagina - 1) * passo, pagina * passo)),
    [linhas, pagina, passo, acumula]);
  return { visiveis, pagina, paginas, setPagina, total, porPagina: passo, acumula };
}

/**
 * O rodapé: «1–50 de 213 · Ordem: prazo ↑», e a paginação quando há mais de
 * uma página. Com `acumula` (o celular), «30 de 213» e, havendo mais,
 * «Mostrar mais» (+30). Quem não passa `acumula` segue a mesma régua do
 * `usePaginacao` — o rodapé e a lista nunca discordam.
 */
export function RodapeDaLista({ pagina, paginas, setPagina, total, porPagina, ordem, teto, acumula }: {
  pagina: number; paginas: number; setPagina(n: number): void; total: number; porPagina: number;
  ordem?: string;
  /** O limite da API: quando a lista bate nele, o rodapé diz que pode haver mais. */
  teto?: number;
  /** A lista acumula (celular): «N de M» e «Mostrar mais», sem setas de página. */
  acumula?: boolean;
}) {
  const estreito = useEstreito();
  if (total === 0) return null;
  const noTeto = teto && total >= teto ? ' (o limite da consulta — refine os filtros)' : '';
  if (acumula ?? estreito) {
    const mostradas = Math.min(total, pagina * porPagina);
    return (
      <div className="rodape-lista acumula">
        <span>{mostradas} de {total}{noTeto}</span>
        {mostradas < total && (
          <button type="button" className="botao secundario mostrar-mais"
            onClick={() => setPagina(pagina + 1)}>Mostrar mais</button>
        )}
      </div>
    );
  }
  const de = (pagina - 1) * porPagina + 1;
  const ate = Math.min(total, pagina * porPagina);
  return (
    <div className="rodape-lista">
      <span>
        {de}–{ate} de {total}
        {noTeto}
        {ordem ? ` · Ordem: ${ordem}` : ''}
      </span>
      {paginas > 1 && (
        <span className="paginacao">
          <button type="button" aria-label="Página anterior" disabled={pagina <= 1}
            onClick={() => setPagina(pagina - 1)}>‹</button>
          <span>{pagina} de {paginas}</span>
          <button type="button" aria-label="Próxima página" disabled={pagina >= paginas}
            onClick={() => setPagina(pagina + 1)}>›</button>
        </span>
      )}
    </div>
  );
}

/**
 * As iniciais do cliente num quadrado — para achar de relance os ensaios
 * do mesmo cliente numa coluna longa. Neutro de propósito: a cor da marca
 * é identidade, não categoria, e não se tinge cliente.
 */
export function Monograma({ nome }: { nome: string | null | undefined }) {
  const iniciais = useMemo(() => {
    if (!nome) return '—';
    const palavras = nome
      .replace(/\b(ltda|s\.?\/?a\.?|me|eireli|epp)\b\.?/gi, '')
      .split(/[\s\-–·]+/)
      .filter((p) => p.length > 2 || /^[A-Z]{2,3}$/.test(p));
    const so = palavras.length ? palavras : [nome];
    if (so.length === 1) return so[0].slice(0, 3).toUpperCase();
    return (so[0][0] + so[1][0]).toUpperCase();
  }, [nome]);
  return <span className="monograma" aria-hidden="true">{iniciais}</span>;
}
